/**
 * cart-recs.js — "Complete the set" complementary recommendations.
 *
 * Strategy:
 *   1. On load, check if [data-cart-recs] containers exist.
 *   2. Fetch /cart.js to get the first line item's product_id.
 *   3. Fetch /recommendations/products?section_id=amphr-cart-recommendations
 *      &product_id=<id>&intent=complementary&limit=<max>
 *      via the Section Rendering API — returns rendered HTML.
 *   4. Inject the rendered HTML into every [data-cart-recs] container.
 *   5. Re-run after the AJAX cart refreshes (listens for the custom
 *      'amphr:cart:refreshed' event; also hooks into amphr-cart.js via
 *      window.amphrCartRecs.refresh() which cart-recs.js exposes so
 *      amphr-cart.js can call it after refreshCart()).
 *
 * No-JS safety: the containers are empty on first server render — no broken UI.
 * Complementary recs require "Complementary products" in Shopify Search & Discovery;
 * if not configured the API returns performed:false or an empty products array and
 * the section outputs nothing, leaving the containers empty (no visible change).
 */

(function () {
  'use strict';

  var SECTION_ID = 'amphr-cart-recommendations';
  var LIMIT = 10;

  /* Cache the last known product_id so we only re-fetch the section HTML
     when the first-item product changes (avoids unnecessary renders). */
  var _lastProductId = null;
  var _pending = false;

  /* ─── Utility ──────────────────────────────────────────────────────────── */
  function containers() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-cart-recs]'));
  }

  /* ─── Main fetch flow ──────────────────────────────────────────────────── */
  function loadRecs() {
    var els = containers();
    if (!els.length) return;
    if (_pending) return;

    _pending = true;

    fetch(((window.routes && window.routes.cart_url) || '/cart') + '.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.item_count || !cart.items || !cart.items[0]) {
          // Cart is now empty — clear any previously injected recs
          clearContainers();
          _lastProductId = null;
          _pending = false;
          return;
        }

        var productId = cart.items[0].product_id;

        // Same product as before — no need to re-fetch the section
        if (productId === _lastProductId) {
          _pending = false;
          return;
        }

        /* Only now do we know a section fetch is actually coming, so the
           skeleton has something to precede. Showing it earlier (before
           /cart.js resolves) flashed a placeholder even when the section
           later returns nothing (no complementary products configured). */
        showSkeletons();

        var url = ((window.routes && window.routes.product_recommendations_url) || '/recommendations/products')
          + '?section_id=' + encodeURIComponent(SECTION_ID)
          + '&product_id=' + encodeURIComponent(productId)
          + '&intent=complementary'
          + '&limit=' + LIMIT;

        /* Security: fetch is same-origin (no credentials leak, no CORS).
           The URL is constructed entirely from trusted constants and a numeric
           product_id sourced from /cart.js (Shopify's own endpoint).
           We verify the response origin matches before injecting. */
        return fetch(url, { credentials: 'same-origin' })
          .then(function (r) {
            if (!r.ok) { throw new Error('recs fetch failed: ' + r.status); }
            /* Extra guard: only accept responses that came from our own origin */
            var responseUrl = r.url || '';
            if (responseUrl && responseUrl.indexOf(window.location.origin) !== 0) {
              throw new Error('recs: unexpected response origin, aborting inject');
            }
            return r.text();
          })
          .then(function (html) {
            _lastProductId = productId;
            injectRecs(html);
          });
      })
      .catch(function (err) {
        console.warn('[cart-recs] loadRecs error:', err);
      })
      .then(function () {
        _pending = false;
      });
  }

  function clearContainers() {
    containers().forEach(function (el) {
      el.setAttribute('aria-busy', 'false');
      el.innerHTML = '';
    });
  }

  function showSkeletons() {
    if (!window.amphrSkeleton) return;
    containers().forEach(function (el) {
      if (el.querySelector('.rec-item')) return;
      el.setAttribute('aria-busy', 'true');
      /* Mirrors amphr-cart-recommendations.liquid's recs-inner/recs-head/recs-rail structure so the skeleton-to-real swap doesn't shift layout. */
      el.innerHTML =
        '<div class="recs-inner">' +
          '<div class="recs-head"><div class="rec-sk-headline sk sk-line">&nbsp;</div></div>' +
          '<div class="recs-rail">' + window.amphrSkeleton.recItem(2) + '</div>' +
        '</div>';
    });
  }

  function injectRecs(html) {
    containers().forEach(function (el) {
      el.setAttribute('aria-busy', 'false');
    });

    /* The Section Rendering API wraps the section output in a <div id="shopify-section-…">.
       We inject that full response into each container; the section's own
       stylesheet tag is included inline so styles load with the content.
       innerHTML is intentional here: the content is Shopify-rendered server HTML
       fetched from the same origin (/recommendations/products?section_id=…).
       It is NOT derived from user input, URL parameters, or untrusted third-party
       sources. The same-origin fetch guard in loadRecs() ensures the response
       came from window.location.origin before this function is called. */
    var trimmed = (html || '').trim();
    if (!trimmed) {
      clearContainers();
      return;
    }

    containers().forEach(function (el) {
      /* Safe: same-origin Shopify section HTML only — see comment above. */
      el.innerHTML = trimmed; // nosemgrep: javascript.browser.security.insecure-document-method.insecure-document-method
    });
  }

  /* ─── Re-run after cart changes ────────────────────────────────────────── */

  /* Option A: custom event fired by amphr-cart.js (if it ever emits one) */
  document.addEventListener('amphr:cart:refreshed', function () {
    _lastProductId = null; // force re-fetch — cart contents may have changed
    loadRecs();
  });

  /* Option B: expose a hook so amphr-cart.js can call it explicitly.
     amphr-cart.js does not currently call this, but it is wired here so a
     future edit to amphr-cart.js can do:
       if (window.amphrCartRecs) window.amphrCartRecs.refresh();
     after its own refreshCart() resolves. */
  window.amphrCartRecs = {
    refresh: function () {
      _lastProductId = null;
      loadRecs();
    }
  };

  /* ─── Initial load ─────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadRecs);
  } else {
    loadRecs();
  }

})();
